import {
  createFileStore,
  isFileDeleted,
  FILE_STORE_KEYS,
  createFileStoreBufferedIO,
  loadChunkedRowsFromFileStore,
  deleteFileStore,
  calculateRowCountFromFileStore,
  buildCsvBlobFromStoredFile,
  readBlobText,
  getUploadS3Path,
  mapGenericUploadFileToFileState,
  sortFilesByUploadOrder,
  _stringArray,
  _excludedRowConditionList,
  _string,
  _number,
  _toArray
} from './helpers'
import { baseLocalForage } from './helpers'
import { Auth } from 'aws-amplify'
import { parse } from 'papaparse'
import { httpGet, httpPost } from '../../../../services/api/AmplifyApi'
import { restApiKey, table } from '../../../../_constants/Code'
import { PathInfoList } from '../../../../_constants/PathInfoList'
import { getDynamoDB } from '../../../../common/utils/DynamoDbUtils'
import { normalizeGenericTaskScreenResponse } from '../../AnalysisData/extra/types'
import {
  DEFAULT_FILE_CLASSIFICATIONS,
  FILE_STATE,
  FILE_TYPE,
  parseResponseOrThrow,
  DataCreateTaskBodySchema,
  FileStateArraySchema,
  GetGenericTaskFilesBodySchema,
  GetPreviousFileSettingsBodySchema,
  IDConversionTableArraySchema,
  CheckFileClassificationInUseBodySchema
} from './types.js'

/** ファイルストアを使用したIndexedDB操作 */

/**
 * ファイルをIndexedDBに保存
 *
 * @param {string} fileKey
 * @param {string} taskKey
 * @param {import('./types').FileState} fileObject
 * @param {import('./types').ParsedFileData} parsedData
 * @returns {Promise<void>}
 */
export const saveFileToIndexDb = async (fileKey, taskKey, fileObject, parsedData) => {
  const fileStore = createFileStore(fileKey, taskKey)

  const records = Array.isArray(parsedData.records) ? parsedData.records : null

  if (records !== null) {
    await fileStore.clear()
  }

  await fileStore.setItem(FILE_STORE_KEYS.REFERENCE, fileObject)
  await fileStore.setItem(FILE_STORE_KEYS.HEADERS, parsedData.headers || [])

  if (records !== null) {
    const bufferedIO = createFileStoreBufferedIO(fileStore, fileObject.fileSize || 0)
    for (const row of records) {
      await bufferedIO.add(row)
    }
    await bufferedIO.finish()
  }

  const allFileKeys = (await baseLocalForage.getItem('all_file_keys')) || {}
  const taskFileKeys = allFileKeys[taskKey] || []
  if (!taskFileKeys.includes(fileKey)) {
    taskFileKeys.push(fileKey)
    allFileKeys[taskKey] = taskFileKeys
    await baseLocalForage.setItem('all_file_keys', allFileKeys)
  }
}

/**
 * ファイルの参照（メタ情報）をIndexedDBに保存する（ファイル種別・キー項目等の変更のみでもstoreと同期するため）
 *
 * @param {string} fileKey
 * @param {string} taskKey
 * @param {import('./types').FileState} fileState
 * @returns {Promise<void>}
 */
export const persistFileReferenceToIndexDb = async (fileKey, taskKey, fileState) => {
  if (!fileKey || !taskKey || !fileState) return
  const fileStore = createFileStore(fileKey, taskKey)
  await fileStore.setItem(FILE_STORE_KEYS.REFERENCE, fileState)
}

/**
 * ファイルをIndexedDBから読み込む
 *
 * @param {string} fileKey
 * @param {string} taskKey
 * @returns {Promise<{ reference: import('./types').FileState; headers: string[]; rows: string[][] } | null>}
 */
export const loadFileFromIndexDb = async (fileKey, taskKey) => {
  const fileStore = createFileStore(fileKey, taskKey)

  const reference = await fileStore.getItem(FILE_STORE_KEYS.REFERENCE)
  const headers = (await fileStore.getItem(FILE_STORE_KEYS.HEADERS)) || []
  const rows = await loadChunkedRowsFromFileStore(fileStore)

  return {
    reference, // Fileオブジェクト
    headers, // ヘッダー配列
    rows // チャンク化された行データ（結合済み）
  }
}

/**
 * ファイルをIndexedDBから削除
 *
 * @param {string} fileKey
 * @param {string} taskKey
 * @returns {Promise<void>}
 */
export const deleteFileFromIndexDb = async (fileKey, taskKey) => {
  // ファイルストアを完全に削除
  await deleteFileStore(fileKey, taskKey)

  // ベースストアからファイルキーを削除
  const allFileKeys = (await baseLocalForage.getItem('all_file_keys')) || {}
  const taskFileKeys = allFileKeys[taskKey] || []
  const updatedKeys = taskFileKeys.filter((key) => key !== fileKey)
  allFileKeys[taskKey] = updatedKeys
  await baseLocalForage.setItem('all_file_keys', allFileKeys)
}

/**
 * 画面表示時にIndexedDB内のファイルデータをすべてクリア（仕様 9.1） indexedDB.databases() が利用可能な場合は file_ / upload_file_ で始まるDBを削除、非対応時は
 * file_keys リストでフォールバック
 *
 * @param {string} [taskKey] - フォールバック時にクリア対象タスク（省略時は全タスクのキーを取得してクリア）
 * @returns {Promise<void>}
 */
export const clearIndexDbAtScreenLoad = async (taskKey) => {
  if (typeof indexedDB === 'undefined' || !indexedDB.databases) {
    await _clearIndexDbFallback(taskKey)
    return
  }
  try {
    const dbs = await indexedDB.databases()
    for (const db of dbs || []) {
      const name = db?.name || ''
      if (name.startsWith('file_') || name.startsWith('upload_file_')) {
        await new Promise((resolve, reject) => {
          const req = indexedDB.deleteDatabase(name)
          req.onsuccess = () => resolve()
          req.onerror = () => reject(req.error)
          req.onblocked = () => resolve()
        }).catch((err) => {
          console.error(`Failed to delete IndexedDB ${name}:`, err)
        })
      }
    }
  } catch (err) {
    console.error('clearIndexDbAtScreenLoad:', err)
    await _clearIndexDbFallback(taskKey)
  }
}

/**
 * @param {string} [taskKey]
 * @returns {Promise<void>}
 */
async function _clearIndexDbFallback(taskKey) {
  const allFileKeys = (await baseLocalForage.getItem('all_file_keys')) || {}
  const tasksToClear = taskKey ? [taskKey] : Object.keys(allFileKeys)
  for (const tk of tasksToClear) {
    const taskFileKeys = allFileKeys[tk] || []
    for (const fileKey of taskFileKeys) {
      try {
        await deleteFileStore(fileKey, tk)
      } catch (error) {
        console.error(`Failed to delete file store for ${fileKey}:`, error)
      }
    }
    delete allFileKeys[tk]
  }
  await baseLocalForage.setItem('all_file_keys', allFileKeys)
}

/**
 * タスクに関連するすべてのファイルストアをクリア（遷移時・登録後など）
 *
 * @param {string} taskKey
 * @returns {Promise<void>}
 */
export const clearIndexDbForTask = async (taskKey) => {
  if (!taskKey) return
  await _clearIndexDbFallback(taskKey)
}

/**
 * S3から既存ファイルをダウンロード時にストアを作成
 *
 * @param {string} fileKey
 * @param {string} taskKey
 * @param {import('./types').FileState} fileObject
 * @param {File} fileData
 * @param {import('./types').ParsedFileData} parsedData
 * @returns {Promise<void>}
 */
export const downloadAndStoreFile = async (fileKey, taskKey, fileObject, fileData, parsedData) => {
  // ダウンロード時にストアを作成してチャンク化保存
  await saveFileToIndexDb(fileKey, taskKey, fileObject, parsedData)

  // ベースストアにファイルキーを追加
  const allFileKeys = (await baseLocalForage.getItem('all_file_keys')) || {}
  const taskFileKeys = allFileKeys[taskKey] || []
  if (!taskFileKeys.includes(fileKey)) {
    taskFileKeys.push(fileKey)
    allFileKeys[taskKey] = taskFileKeys
    await baseLocalForage.setItem('all_file_keys', allFileKeys)
  }
}

/** API（FileManagement） */

/** @param {unknown} rows @returns {boolean} */
function _hasRows(rows) {
  if (Array.isArray(rows)) return rows.length > 0
  return Boolean(rows)
}

/**
 * データ作成タスク情報を取得（IF2005 汎用データ作成タスク取得）
 *
 * @param {string} taskKey
 * @param {string} corporationNumber
 * @returns {Promise<{ body: import('./types').DataCreateTaskBody }>}
 */
export const fetchDataCreateTask = async (taskKey, corporationNumber) => {
  const data = await getDynamoDB(
    table.DataCreateTask,
    null,
    corporationNumber,
    { CorporationNumber: corporationNumber, Key: Number(taskKey) },
    { DeleteFlag: 0 }
  )
  const [row] = Array.isArray(data) ? data : []
  const body = {
    periodKind: _string(row?.PeriodKind ?? row?.periodKind),
    periodValue: _string(row?.PeriodValue ?? row?.periodValue),
    taskKey: _string(taskKey)
  }
  const parsed = parseResponseOrThrow(DataCreateTaskBodySchema, body)
  return { body: parsed }
}

/**
 * AppMaster / get_app_master の fileClassification レスポンスを行配列に正規化（guard clause で早期 return）
 *
 * @param {unknown} data - Get_app_master API または getDynamoDB の戻り値
 * @returns {unknown[]}
 */
function _normalizeAppMasterFileClassificationRows(data) {
  if (Array.isArray(data)) return data
  if (data?.body && Array.isArray(data.body)) return data.body
  if (data?.Items && Array.isArray(data.Items)) return data.Items
  if (data && (data.FileClassificationLabel != null || data.file_classification_label != null)) return [data]
  return []
}

/** ValueConversion の getDynamoDB 戻り値を行配列に正規化 */
function _normalizeValueConversionTableRows(data) {
  if (Array.isArray(data)) return data
  if (data?.body && Array.isArray(data.body)) return data.body
  if (data?.Items && Array.isArray(data.Items)) return data.Items
  if (data && (data.ConversionTableKey != null || data.conversion_table_key != null)) return [data]
  return []
}

/**
 * ファイル種別リストを取得（IF020100 ファイル種別取得）
 *
 * @param {string} corporationNumber
 * @returns {Promise<{ body: import('./types').FileClassificationListStructure }>}
 */
export const fetchFileClassificationList = async (corporationNumber) => {
  const res = await httpGet(
    restApiKey.CHROFY_REST_API,
    PathInfoList.Resource.getFileClassification(corporationNumber),
    { headers: {} }
  )

  const data = _toArray(res)

  const mappedData = data.map((item) => ({
    fileClassification: _string(item.file_classification_label),
    isDefault: Boolean(item.is_default_file_classification),
    isInUse: false
  }))

  return {
    body: {
      defaultTypes: mappedData.filter((item) => item.isDefault),
      userTypes: mappedData.filter((item) => !item.isDefault)
    }
  }
}

/**
 * 既存ファイル情報を取得（IF02101 汎用マッピングファイル情報取得）
 *
 * @param {string} taskKey
 * @param {string} corporationNumber
 * @returns {Promise<{ body: import('./types').FileState[]; usedMappingFileTypes: string[] }>}
 */
export const fetchExistingFiles = async (taskKey, corporationNumber) => {
  const res = await httpGet(restApiKey.CHROFY_REST_API, PathInfoList.Resource.getGenericTaskFiles(corporationNumber), {
    headers: {},
    queryStringParameters: {
      data_create_task_key: Number(taskKey)
    }
  })

  const rawBody = res?.body ?? res
  if (!rawBody || typeof rawBody !== 'object' || Array.isArray(rawBody)) {
    parseResponseOrThrow(GetGenericTaskFilesBodySchema, rawBody, 'IF02101 レスポンス形式が不正です')
  }

  const usedMappingFileTypes = _stringArray(rawBody.used_mapping_file_types ?? rawBody.usedMappingFileTypes)
  const uploadRows = _toArray(rawBody.upload_files ?? rawBody.uploadFiles)
  const files = uploadRows.map((row) => mapGenericUploadFileToFileState(row))
  const parsedFiles = parseResponseOrThrow(FileStateArraySchema, files)
  // API は SearchKey（末尾が nanoid の fileKey）昇順で返すため、アップロード順に並べ替える
  const orderedFiles = sortFilesByUploadOrder(parsedFiles)
  parseResponseOrThrow(GetGenericTaskFilesBodySchema, {
    upload_files: orderedFiles,
    used_mapping_file_types: usedMappingFileTypes
  })
  return { body: orderedFiles, usedMappingFileTypes }
}

/**
 * 過去タスクファイル設定を取得（IF02104 過去タスクファイル設定取得）。 同じファイル種別を最後に使用した過去タスクの設定を取得する。同名列の照合は画面側（helpers の applyPreviousSettings）で行う。
 *
 * @param {string} corporationNumber
 * @param {string} taskKey - 現在のデータ作成タスクキー（このキーより小さいタスクのみ検索対象）
 * @param {string} fileClassification - 選択されたファイル種別のラベル
 * @returns {Promise<{ found: boolean; settings: import('./types').PreviousFileSettings | null }>}
 */
export const fetchPreviousFileSettings = async (corporationNumber, taskKey, fileClassification) => {
  const res = await httpGet(
    restApiKey.CHROFY_REST_API,
    PathInfoList.Resource.getPreviousFileSettings(corporationNumber),
    {
      headers: {},
      queryStringParameters: {
        file_classification: _string(fileClassification),
        data_create_task_key: _string(taskKey)
      }
    }
  )

  const rawBody = res?.body ?? res
  const rawSettings = rawBody?.settings
  const found = Boolean(rawBody?.found) && rawSettings != null

  // IF02104 Response: JoinColumnList は1要素想定のため先頭要素を平坦化して正規化する
  const joinFirst = _toArray(rawSettings?.join_column_list ?? rawSettings?.joinColumnList)[0] ?? {}
  const settings = found
    ? {
        dataCategory: _string(rawSettings.data_category ?? rawSettings.dataCategory),
        primaryKey: _string(joinFirst.primary_key ?? joinFirst.primaryKey),
        conversionTableKey: _string(joinFirst.conversion_table_key ?? joinFirst.conversionTableKey),
        excludedRowConditionList: _excludedRowConditionList(
          rawSettings.excluded_row_condition_list ?? rawSettings.excludedRowConditionList
        ),
        hashColumnList: _stringArray(rawSettings.hash_column_list ?? rawSettings.hashColumnList),
        isBaseFile: Boolean(rawSettings.is_base_file ?? rawSettings.isBaseFile)
      }
    : null

  return parseResponseOrThrow(
    GetPreviousFileSettingsBodySchema,
    { found, settings },
    'IF02104 レスポンス形式が不正です'
  )
}

/**
 * ID変換テーブルリストを取得（IF02102）
 *
 * @param {string} corporationNumber
 * @returns {Promise<{ body: import('./types').IDConversionTable[] }>}
 */
export const fetchIDConversionTables = async (corporationNumber) => {
  const res = await httpGet(restApiKey.CHROFY_REST_API, PathInfoList.Resource.listConversionTables(corporationNumber), {
    headers: {}
  })

  const list = res.conversion_table_list.map((row) => {
    const updatedAt =
      row.conversion_table_update_date_time != null
        ? row.conversion_table_update_date_time
        : row.conversion_table_create_date_time

    return {
      id: _string(row.conversion_table_key),
      name: _string(row.conversion_table_name),
      description: _string(row.conversion_table_memo),
      recordCount: _number(row.conversion_table_record_count),
      updatedAt: _string(updatedAt),
      mappings: []
    }
  })

  const parsed = parseResponseOrThrow(IDConversionTableArraySchema, list)
  return { body: parsed }
}

/**
 * 変換表項目取得（IF02077）
 *
 * 変換項目本体（最大10万件）は Lambda 同期レスポンスの 6MB 上限・API Gateway の 29秒タイムアウトを 超えるため、取得APIは本体を返さず S3 キャッシュファイルのキー（s3_key）のみを返す。
 * ここでは署名付きURLを取得して S3 から直接ダウンロードし、本体 JSON を返す。 （conversionTableInfo/extra/services.js の getConversionValues と同方式）
 *
 * @param {string} corporationNumber
 * @param {string} conversionTableKey
 * @returns {Promise<any>}
 */
const _fetchViaPresignedUrl = async (corporationNumber, objectKeyName, errorMessages) => {
  const presignedRes = await httpGet(
    restApiKey.CHROFY_REST_API,
    PathInfoList.Resource.getS3PresignedUrl(corporationNumber),
    {
      headers: {},
      queryStringParameters: { object_key_name: objectKeyName }
    }
  )

  const presignedUrl = presignedRes?.presigned_url ?? presignedRes?.body?.presigned_url
  if (!presignedUrl) {
    throw new Error(errorMessages.noUrl)
  }

  const fetchRes = await fetch(presignedUrl)
  if (!fetchRes.ok) {
    throw new Error(`${errorMessages.fetchFailed}: ${fetchRes.status}`)
  }

  return fetchRes
}

export const fetchConversionValues = async (corporationNumber, conversionTableKey) => {
  const init = {
    headers: {},
    queryStringParameters: {
      conversion_table_key: conversionTableKey
    }
  }

  const res = await httpGet(
    restApiKey.CHROFY_REST_API,
    PathInfoList.Resource.getConversionValues(corporationNumber),
    init
  )

  const s3Key = res?.s3_key ?? res?.body?.s3_key
  if (!s3Key) {
    throw new Error('変換項目のS3キーの取得に失敗しました')
  }

  const fetchRes = await _fetchViaPresignedUrl(corporationNumber, s3Key, {
    noUrl: '変換項目ファイルの署名付きURLの取得に失敗しました',
    fetchFailed: '変換項目ファイルの取得に失敗しました'
  })

  return await fetchRes.json()
}

/** 取得中の変換表項目（変換表単位）。同一変換表への同時リクエストで同じPromiseを共有する */
const _conversionValuesInFlight = new Map()

/**
 * 取得中リクエストの状態をリセットする（画面離脱時に呼ぶ）
 *
 * @returns {void}
 */
export const resetConversionValuesInFlight = () => {
  _conversionValuesInFlight.clear()
}

/**
 * 変換表項目を取得する（変換表単位で重複取得を防ぐ）
 *
 * 取得済み・取得中のPromiseを変換表単位で保持し、複数ファイルが同じ変換表を使う場合でも S3からのダウンロードを1回のみとする。画面離脱時は resetConversionValuesInFlight で破棄する。
 *
 * @param {string} corporationNumber
 * @param {string} conversionTableKey
 * @returns {Promise<any>}
 */
export const getConversionValuesForTable = async (corporationNumber, conversionTableKey) => {
  const mapKey = `${corporationNumber}:${conversionTableKey}`

  const inFlight = _conversionValuesInFlight.get(mapKey)
  if (inFlight) {
    return await inFlight
  }

  // await を挟まずに Promise を登録する。そうしないと同時実行時に
  // 両方が未登録と判断してS3から二重取得してしまう
  const promise = fetchConversionValues(corporationNumber, conversionTableKey)
  _conversionValuesInFlight.set(mapKey, promise)

  try {
    return await promise
  } catch (error) {
    // 失敗を永続的に保持しない（再試行できるようにする）。
    // 画面離脱でリセットされた後に古い取得が失敗することがあるため、
    // 自分が登録した Promise の場合のみ削除する（新しい取得を消さない）
    if (_conversionValuesInFlight.get(mapKey) === promise) {
      _conversionValuesInFlight.delete(mapKey)
    }
    throw error
  }
}

/**
 * S3からファイルをダウンロード（Corporation S3）
 *
 * @param {string} corporationNumber
 * @param {string} taskKey
 * @param {string} fileKey
 * @param {string} fileState
 * @param {import('./types').FileState} fileObject
 * @returns {Promise<{ fileData: Blob; headers: string[] }>}
 */
export const downloadFileFromS3 = async (corporationNumber, taskKey, fileKey, fileState, fileObject) => {
  const s3Path = getUploadS3Path(taskKey, fileKey)

  // FileManagementでは downloadObject(/s3_object) を使わず、署名URLで取得する（DataMapperと同方針）
  const init = {
    headers: {},
    queryStringParameters: {
      object_key_name: s3Path
    }
  }

  const presignedRes = await httpGet(
    restApiKey.CHROFY_REST_API,
    PathInfoList.Resource.getS3PresignedUrl(corporationNumber),
    init
  )

  const presignedUrl = presignedRes?.presigned_url
  if (!presignedUrl) {
    throw new Error('Failed to get presigned url')
  }

  const fetchRes = await fetch(presignedUrl)
  if (!fetchRes.ok) {
    throw new Error(`Failed to fetch file via presigned url: ${fetchRes.status}`)
  }

  const fileData = await fetchRes.blob()

  // CSVをパースしてヘッダーを取得（ワーカーを使用）
  const text = await fileData.text()

  // 大きなファイルの場合はワーカーでチャンク処理
  const parseResult = await parseCsvWithWorker(text)

  return {
    fileData,
    headers: parseResult.headers
  }
}

/**
 * ファイルをS3にアップロード（Private S3）
 *
 * @param {string} taskKey
 * @param {string} fileKey
 * @param {Blob} fileBlob
 * @returns {Promise<{ body: { success: boolean; s3Path: string } }>}
 */
export const uploadFileToS3 = async (taskKey, fileKey, fileBlob) => {
  const s3Path = getUploadS3Path(taskKey, fileKey)

  // Amplify Storageを使用してPrivate S3にアップロード
  const { Storage } = await import('aws-amplify')
  await Storage.put(s3Path, fileBlob, {
    level: 'private',
    contentType: 'text/csv'
  })

  return {
    body: {
      success: true,
      s3Path: s3Path
    }
  }
}

/**
 * 初期データ取得（タスク・種別・既存ファイル・変換表）
 *
 * @param {string} taskKey
 * @param {string} corporationNumber
 * @returns {Promise<[any, any, any, any]>}
 */
export const loadInitialData = async (taskKey, corporationNumber) => {
  return await Promise.all([
    fetchDataCreateTask(taskKey, corporationNumber),
    fetchFileClassificationList(corporationNumber),
    fetchExistingFiles(taskKey, corporationNumber),
    fetchIDConversionTables(corporationNumber)
  ])
}

/**
 * 既存ファイル1件をS3から取得してIndexedDBに保存し、パース済みデータを返す
 *
 * @param {import('./types').FileState} file
 * @param {string} taskKey
 * @param {string} corporationNumber
 * @returns {Promise<{ headers: string[]; isStoredLocally: boolean }>}
 */
export const downloadAndProcessExistingFile = async (file, taskKey, corporationNumber) => {
  const { fileData: downloadedBlob, headers } = await downloadFileFromS3(
    corporationNumber,
    taskKey,
    file.fileKey,
    file.fileState,
    file
  )

  const text = await readBlobText(downloadedBlob)
  const parseResult = await parseCsvWithWorker(text)
  const parsedHeaders = headers.length > 0 ? headers : parseResult.headers

  const parsedData = {
    headers: parsedHeaders,
    records: parseResult.records,
    recordCount: parseResult.totalRows,
    sheetName: file.sheetName || '',
    sheetList: file.sheetList || []
  }

  await saveFileToIndexDb(file.fileKey, taskKey, file, parsedData)

  return {
    headers: parsedHeaders,
    isStoredLocally: true
  }
}

/**
 * Pending-virus-check のファイルをS3にアップロードする
 *
 * @param {import('./types').FileState[]} changedFiles
 * @param {string} taskKey
 * @returns {Promise<void>}
 */
export const uploadPendingFilesToS3 = async (changedFiles, taskKey) => {
  for (const file of changedFiles) {
    if (file.fileState !== FILE_STATE.PENDING_VIRUS_CHECK) continue
    const fileData = await loadFileFromIndexDb(file.fileKey, taskKey)
    if (!fileData || !fileData.rows) continue
    const fileBlob = buildCsvBlobFromStoredFile(fileData.headers || [], fileData.rows)
    try {
      await uploadFileToS3(taskKey, file.fileKey, fileBlob)
    } catch (err) {
      console.error('uploadPendingFilesToS3:', err)
      console.error(err?.stack ?? err)
      err.fileName = file.fileName
      throw err
    }
  }
}

/**
 * @param {import('./types').FileState} file
 * @returns {string}
 */
function _getFileKind(file) {
  if (file?.fileType === FILE_TYPE.CSV) return FILE_TYPE.CSV
  return FILE_TYPE.EXCEL
}

/**
 * ファイルを登録（IF2080 汎用マッピングファイル管理更新）
 *
 * @param {string} taskKey
 * @param {string} corporationNumber
 * @param {import('./types').FileState[]} changedFiles
 * @param {Record<string, import('./types').FileState> | null} savedUploadState
 * @returns {Promise<{ body: { messages?: string[] } }>}
 */
export const registerFiles = async (taskKey, corporationNumber, changedFiles, savedUploadState = null) => {
  // IF02080: user_id_sub は Cognito Identity Pool の identity ID（形式 ap-northeast-1:<uuid>）
  const credentials = await Auth.currentUserCredentials()
  const identityPoolIdentityId = credentials?.identityId ?? ''

  const files = []

  for (const file of changedFiles || []) {
    if (!file) continue

    // pending-deletion は最小フィールドのみ
    if (isFileDeleted(file.fileState)) {
      files.push({
        file_key: file.fileKey,
        file_status: FILE_STATE.PENDING_DELETION
      })
      continue
    }

    const fileStore = createFileStore(file.fileKey, taskKey)
    // ローカルにファイルデータがない場合（ウイルスチェック未完了・ダウンロード未実施等）は、
    // 0 で数え直さず取得済みの行数（IF02101 row_count）を維持する
    const localTotalRows = await calculateRowCountFromFileStore(fileStore)
    const rowCount = localTotalRows > 0 ? Math.max(0, localTotalRows - 1) : _number(file.rowCount)

    files.push({
      file_key: file.fileKey,
      file_status: file.fileState,
      file_name: file.fileName,
      file_kind: _getFileKind(file),
      file_classification: file.fileClassification,
      row_count: rowCount,
      file_size: file.fileSize,
      file_upload_date_time: _string(file.uploadedDateTime),
      sheet_name: _string(file.sheetName),
      file_headers: Array.isArray(file.headers) ? file.headers : [],
      file_previous_status: _string(savedUploadState?.[file.fileKey]?.fileState ?? ''),
      conversion_table_key: _string(file.idConversionTable),
      excluded_row_condition_list: _toArray(file.exclusionConditions),
      hash_column_list: Array.isArray(file.hashColumns) ? file.hashColumns : [],
      primary_key_col: _string(file.primaryKeyColumn),
      data_category: file.dataCategory || null,
      is_base_file: Boolean(file.isBaseFile)
    })
  }

  const init = {
    body: {
      data_create_task_key: taskKey,
      user_id_sub: identityPoolIdentityId,
      files
    },
    headers: {}
  }

  return await httpPost(
    restApiKey.CHROFY_REST_API,
    PathInfoList.Resource.updateGenericTaskFiles(corporationNumber),
    init
  )
}

/**
 * ファイル種別を一括保存（IF2079 ファイル種別情報更新）
 *
 * @param {string} corporationNumber
 * @param {{ file_classification_label: string; delete_flag: boolean }[]} bodyArray
 * @returns {Promise<any>}
 */
export const saveFileClassifications = async (corporationNumber, bodyArray) => {
  const init = {
    body: bodyArray,
    headers: {}
  }

  return await httpPost(
    restApiKey.CHROFY_REST_API,
    PathInfoList.Resource.updateFileClassification(corporationNumber),
    init
  )
}

/**
 * CSV文字列を引用符対応パーサ（PapaParse）でパースする。
 *
 * アップロード直後の表示と同じ PapaParse を使うことで、セル内改行（引用符で 囲まれた改行）をレコード区切りと誤認しないようにする。worker オプションで 大容量でも UI
 * をブロックしない。先頭行をヘッダー、以降をレコードとして返す。
 *
 * 既定では空行をスキップする（アップロード表示の従来挙動）。マッピング結果プレビューのように、変換失敗で全セルが空になった行も1データ行として保持したい場合は `keepEmptyLines: true` を指定する。PapaParse
 * は `skipEmptyLines:false` のとき CSV 末尾の改行に対しても空レコードを1件生成するため、pandas
 * 等が各行末に改行を付けた出力（末尾改行あり）では行数が1件過剰になる。これを防ぐため、空行保持時は末尾の改行を1つだけ落としてからパースする。
 *
 * @param {string} csvText
 * @param {{ keepEmptyLines?: boolean }} [options] - KeepEmptyLines: 空行を保持するか（既定 false）
 * @returns {Promise<{ headers: string[]; records: string[][]; totalRows: number }>}
 */
export const parseCsvWithWorker = (csvText, options = {}) => {
  const keepEmptyLines = options.keepEmptyLines === true
  return new Promise((resolve, reject) => {
    const raw = typeof csvText === 'string' ? csvText : ''
    // 先頭のBOM（U+FEFF, utf-8-sig）を除去してからパースする
    const withoutBom = raw.charCodeAt(0) === 0xfeff ? raw.slice(1) : raw
    // 空行保持時は、末尾改行由来の余分な空レコード混入を防ぐため末尾改行を1つ落とす
    const text = keepEmptyLines ? withoutBom.replace(/\r?\n$/, '') : withoutBom
    if (text.trim() === '') {
      resolve({ headers: [], records: [], totalRows: 0 })
      return
    }

    parse(text, {
      worker: true,
      skipEmptyLines: !keepEmptyLines,
      complete: (results) => {
        const data = Array.isArray(results?.data) ? results.data : []
        const [headers = [], ...records] = data
        resolve({ headers, records, totalRows: records.length })
      },
      error: (err) => {
        console.error('CSV parse error:', err)
        console.error(err?.stack ?? err)
        reject(err instanceof Error ? err : new Error(String(err)))
      }
    })
  })
}

/**
 * ファイル種別が使用中かチェック
 *
 * @param {string} fileClassificationLabel
 * @param {string} corporationNumber
 * @returns {Promise<{ body: { isInUse: boolean } }>}
 */
/**
 * IF02095: レポートデータ更新画面情報取得（ファイル管理からのポーリング用）
 *
 * @param {string} corporationNumber
 * @param {string | number} dataCreateTaskKey
 * @returns {Promise<import('../../AnalysisData/extra/types').GenericTaskScreenNormalized>}
 */
export async function fetchGenericTaskScreenForTask(corporationNumber, dataCreateTaskKey) {
  const init = {
    headers: {},
    queryStringParameters: {
      data_create_task_key: Number(dataCreateTaskKey)
    }
  }
  const raw = await httpGet(
    restApiKey.CHROFY_REST_API,
    PathInfoList.Resource.getGenericTaskScreen(corporationNumber),
    init
  )
  return normalizeGenericTaskScreenResponse(raw)
}

export const checkFileClassificationInUse = async (fileClassificationLabel, corporationNumber) => {
  const rows = await getDynamoDB(
    table.GenericUploadFile,
    null,
    corporationNumber,
    { CorporationNumber: corporationNumber },
    { EntityType: 'UploadFile', FileClassification: fileClassificationLabel },
    { ProjectionExpression: 'SearchKey' }
  )

  const body = { isInUse: _hasRows(rows) }
  const parsed = parseResponseOrThrow(CheckFileClassificationInUseBodySchema, body)
  return { body: parsed }
}
